<script setup>
import { useI18n } from 'vue-i18n';
import { useTestsStore } from '@stores';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useDurationFormatInput } from '@utils';

const { t } = useI18n({ useScope: 'global' });
const testsStore = useTestsStore();
const { main, currentId } = storeToRefs(testsStore);

const visible = ref(false);
const sessions = ref([]);

const resolver = () => {
    const errors = {};
    if (!main.value.name) {
        errors.name = [{ message: t('userCreate.required') }];
    }
    if (!main.value.selectedLanguages.length) {
        errors.languages = [{ message: t('userCreate.required') }];
    }
    if (!main.value.duration) {
        errors.duration = [{ message: t('userCreate.required') }];
    }
    return {
        errors,
    };
};

const setData = ({ valid }) => {
    if (!valid) return;
    testsStore.updateTest();
};

const configChange = (autoReveal) => {
    main.value.lockAnswer = autoReveal;
};

const inActiveConfig = async () => {
    sessions.value = await testsStore.checkSession(currentId.value);
    if (sessions.value.length) {
        visible.value = true;
        main.value.isActive = true;
    }
};

const durationHandler = (e) => {
    main.value.duration = useDurationFormatInput(e.target.value);
};
</script>

<template>
    <Card>
        <template #content>
            <Form v-slot="$form" @submit="setData" :resolver="resolver" class="test-wrapper">
                <div class="input-wrapper">
                    <label for="name">{{ t('tests.name') }}</label>
                    <InputText id="name" name="name" :placeholder="t('tests.enterTest')" v-model="main.name" fluid />
                    <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">{{ $form.name.error?.message }}</Message>
                </div>

                <div class="input-wrapper">
                    <label>{{ t('tests.description') }}</label>
                    <InputText name="description" :placeholder="t('tests.enterDesc')" v-model="main.description" fluid />
                </div>
                <div class="input-wrapper-grid">
                    <div class="input-wrapper">
                        <label>{{ t('tests.questionCount') }}</label>
                        <InputNumber name="questionCount" :placeholder="t('tests.questionCount')" v-model="main.questionCount" fluid :min="1" />
                    </div>
                    <div class="input-wrapper">
                        <label>{{ t('tests.passingPercent') }}</label>
                        <InputGroup>
                            <InputNumber name="passingPercent" :placeholder="t('tests.passingPercent')" v-model="main.passingPercent" fluid :min="0" :max="100" />
                            <InputGroupAddon>%</InputGroupAddon>
                        </InputGroup>
                    </div>
                    <div class="input-wrapper">
                        <label id="languages">{{ t('tests.languages') }}</label>
                        <MultiSelect for="languages" optionValue="code" name="languages" v-model="main.selectedLanguages" :options="main.languages" optionLabel="name" filter :placeholder="t('tests.selectLangs')" />
                        <Message v-if="$form.languages?.invalid" severity="error" size="small" variant="simple">{{ $form.languages.error?.message }}</Message>
                    </div>
                    <div class="input-wrapper">
                        <label for="duration">{{ t('global.duration') }}</label>
                        <InputText id="duration" name="duration" :value="main.duration" @input="durationHandler" placeholder="HH:MM:SS" inputmode="numeric" />
                        <Message v-if="$form.duration?.invalid" severity="error" size="small" variant="simple">{{ $form.duration.error?.message }}</Message>
                    </div>
                </div>

                <div class="toggle-container">
                    <div class="toggle-wrapper">
                        <ToggleSwitch @change="inActiveConfig" v-model="main.isActive" inputId="active" />
                        <label for="active"> {{ t('tests.isActive') }} </label>
                    </div>
                    <div class="toggle-wrapper">
                        <ToggleSwitch @change="configChange(main.autoReveal)" v-model="main.autoReveal" inputId="reveal" />
                        <label for="reveal">{{ t('tests.autoReveal') }}</label>
                    </div>
                    <div class="toggle-wrapper">
                        <ToggleSwitch v-model="main.shuffleQuestions" inputId="shuffle" />
                        <label for="shuffle">{{ t('tests.shuffleQuestion') }}</label>
                    </div>
                    <div class="toggle-wrapper">
                        <ToggleSwitch v-model="main.lockAnswer" inputId="lock" :disabled="main.autoReveal" />
                        <label for="lock">{{ t('tests.lockAnswer') }}</label>
                    </div>
                    <div class="toggle-wrapper">
                        <ToggleSwitch v-model="main.answersRequired" inputId="required" />
                        <label for="required">{{ t('tests.answersRequired') }}</label>
                    </div>
                </div>

                <Button :label="t('global.save')" type="submit" />
            </Form>
            <Dialog v-model:visible="visible" modal :header="t('tests.attachedSessions')" :style="{ width: '30rem' }">
                <p class="dialog-header">
                    <i class="pi pi-exclamation-triangle !text-2xl"></i>
                    <span class="font-semibold">{{ t('tests.testWarning') }}</span>
                </p>
                <div class="flex flex-col gap-4 mb-4 justify-center">
                    <router-link v-for="session in sessions" :key="session" class="text-xl hover:underline text-center" :to="`/sessions?id=${session.id}`" target="_blank">
                        {{ session.name }}
                        <i class="pi pi-external-link" />
                    </router-link>
                </div>
            </Dialog>
        </template>
    </Card>
</template>

<style scoped>
.test-wrapper {
    @apply flex flex-col gap-4;
}
.input-wrapper {
    @apply flex flex-col gap-2 w-full;
}
.input-wrapper label {
    @apply line-clamp-1;
}
.toggle-container {
    @apply flex flex-col gap-4 my-4;
}
.toggle-wrapper {
    @apply flex items-center gap-2;
}
.input-wrapper-grid {
    @apply grid gap-4 grid-cols-4;
}
.dialog-header {
    @apply flex items-start gap-4 mb-8 text-yellow-600 text-lg;
}
</style>
